import { useMemo, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faUpDownLeftRight, faPaste, faArrowPointer } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'sonner';

import { urlsListIsValid, urlsToFiles } from '../../../../utils/urls';
import { pluralize } from '../../../../utils/strings';
import Toast from '../Toast';
import CompressionModal from './CompressionModal';
import FileRow from './FileRow';
import {
  type FileMeta,
  defaultMeta,
  stripExtension,
  formatToExt,
  originalExt,
  compressImage,
} from './imageCompression';

interface UploadModalProps {
  onUploaded: (options: any[]) => void;
  onClose: () => void;
}

export default function UploadModal({ onUploaded, onClose }: UploadModalProps) {
  const [files, setFiles] = useState<File[]>([]);
  const [filesMeta, setFilesMeta] = useState<FileMeta[]>([]);
  const [modalIndex, setModalIndex] = useState<number | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // URL fetch state
  const [filesUrls, setFilesUrls] = useState('');
  const [fetchingFiles, setFetchingFiles] = useState(false);
  const urlsAreValid = useMemo(() => urlsListIsValid(filesUrls), [filesUrls]);

  // Drop zone state
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addFiles = (incoming: File[]) => {
    setFiles((prev) => [...prev, ...incoming]);
    setFilesMeta((prev) => [...prev, ...incoming.map(defaultMeta)]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
    setFilesMeta((prev) => prev.filter((_, i) => i !== index));
  };

  const updateMeta = (index: number, patch: Partial<FileMeta>) => {
    setFilesMeta((prev) => prev.map((m, i) => (i === index ? { ...m, ...patch } : m)));
  };

  // Recompute compressedSize when compression settings change
  const compressKey = filesMeta.map((m) => `${m.compress}-${m.quality}-${m.format}`).join('|');
  const sizeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (sizeDebounceRef.current) clearTimeout(sizeDebounceRef.current);
    sizeDebounceRef.current = setTimeout(async () => {
      const updates = await Promise.all(
        filesMeta.map(async (meta, i) => {
          const file = files[i];
          if (!file || !meta.compress || !file.type.startsWith('image/')) return null;
          try {
            const out = await compressImage(file, meta.quality / 100, meta.format);
            return { i, size: out.size };
          } catch { return null; }
        }),
      );
      setFilesMeta((prev) =>
        prev.map((m, i) => {
          const u = updates.find((u) => u?.i === i);
          if (u) return { ...m, compressedSize: u.size };
          if (!m.compress) return { ...m, compressedSize: null };
          return m;
        }),
      );
    }, 400);
  }, [compressKey, files.length]);

  const fetchFiles = async () => {
    setFetchingFiles(true);
    try {
      const newFiles = await urlsToFiles(filesUrls);
      addFiles(newFiles);
      setFilesUrls('');
      toast.custom((id) => (
        <Toast id={id} level="success" message={`${newFiles.length} ${pluralize(newFiles.length, 'file')} fetched successfully.`} />
      ));
    } catch (err) {
      toast.custom((id) => <Toast id={id} level="error" message={String(err)} />);
    } finally {
      setFetchingFiles(false);
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const active = document.activeElement;
    if (active?.tagName === 'TEXTAREA') return;
    for (const item of e.clipboardData.items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          e.preventDefault();
          addFiles([file]);
          toast.custom((id) => <Toast id={id} level="success" message={`File "${file.name}" added.`} />);
        }
      }
    }
  };

  const handleSubmit = async () => {
    if (!files.length) return;
    setIsProcessing(true);
    setError(null);
    try {
      const processedFiles = await Promise.all(
        files.map(async (file, index) => {
          const meta = filesMeta[index];
          let out = file;
          if (meta?.compress && file.type.startsWith('image/')) {
            out = await compressImage(file, meta.quality / 100, meta.format);
          }
          const ext = meta?.compress && file.type.startsWith('image/')
            ? formatToExt(meta.format)
            : originalExt(file.name);
          const finalName = (meta?.name || stripExtension(file.name)) + ext;
          return finalName !== out.name ? new File([out], finalName, { type: out.type }) : out;
        }),
      );

      const formData = new FormData();
      processedFiles.forEach((file, i) => formData.append(`admin_attachments[files][${i}]`, file));

      const csrfToken = document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content;
      const response = await axios.post('/admin/attachments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Accept': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
      });

      onUploaded(response.data);
    } catch (err: any) {
      setError(err?.response?.data?.message || String(err));
    } finally {
      setIsProcessing(false);
    }
  };

  const loading = isProcessing || fetchingFiles;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onPaste={handlePaste}
    >
      <div
        className="shadow-xl w-full mx-4 flex flex-col"
        style={{ maxWidth: 540, maxHeight: '90vh', backgroundColor: '#f5f5f5' }}
      >
        {modalIndex !== null && filesMeta[modalIndex] && (
          <CompressionModal
            meta={filesMeta[modalIndex]}
            file={files[modalIndex]}
            onSave={(patch) => updateMeta(modalIndex, patch)}
            onClose={() => setModalIndex(null)}
          />
        )}

        {/* Header */}
        <div style={{ padding: '8px 12px', borderBottom: '1px solid #ddd', fontWeight: 'bold', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span>Upload new attachment</span>
          <button type="button" className="btn btn-sm btn-ghost btn-circle" onClick={onClose}>✕</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>

          {/* URL fetch */}
          <div style={{ backgroundColor: 'var(--color-base-300)', padding: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <div className="text-xs font-bold">Fetch from URL</div>
            <textarea
              className="textarea textarea-bordered textarea-sm w-full"
              style={{ backgroundColor: 'var(--color-base-200)', resize: 'vertical' }}
              rows={2}
              placeholder={'https://example.com/image.jpg\nhttps://example.com/file.pdf'}
              value={filesUrls}
              onChange={(e) => setFilesUrls(e.target.value)}
            />
            <div
              className="tooltip tooltip-right w-fit"
              data-tip={!filesUrls ? 'Enter URLs to fetch files' : !urlsAreValid ? 'One or more URLs are invalid' : 'Fetch files from the provided URLs'}
            >
              <button
                type="button"
                className="btn btn-primary btn-sm"
                disabled={loading || !urlsAreValid}
                onClick={fetchFiles}
              >
                {fetchingFiles ? 'Fetching...' : 'Fetch files'}
              </button>
            </div>
          </div>

          <div className="divider text-sm my-0">OR</div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
            onDrop={(e) => { e.preventDefault(); setIsDragging(false); addFiles(Array.from(e.dataTransfer.files)); }}
            onClick={() => fileInputRef.current?.click()}
            className="bg-base-200 grid place-items-center p-8 cursor-pointer transition-all"
            style={{ border: isDragging ? '1px solid #570df8' : '1px solid #d6cac1', backgroundColor: isDragging ? '#f0e6ff' : undefined }}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={(e) => { if (e.target.files) { addFiles(Array.from(e.target.files)); e.target.value = ''; } }}
              style={{ display: 'none' }}
              accept="image/*,audio/*,video/*,application/pdf,.doc,.docx"
            />
            <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 mb-2" style={{ color: isDragging ? '#570df8' : '#999' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
            <div className="text-sm mb-1">Drag & drop, paste, or click to select</div>
            <div className="flex gap-1 text-base-content/50">
              <FontAwesomeIcon icon={faUpDownLeftRight} />
              <FontAwesomeIcon icon={faPaste} />
              <FontAwesomeIcon icon={faArrowPointer} />
            </div>
          </div>

          {/* File list */}
          {files.length > 0 && (
            <>
              <div className="divider text-sm my-0">Files ({files.length})</div>
              <div className="flex flex-col gap-2">
                {files.map((file, index) => (
                  <FileRow
                    key={`${file.name}-${index}`}
                    file={file}
                    meta={filesMeta[index] ?? defaultMeta(file)}
                    onUpdate={(patch) => updateMeta(index, patch)}
                    onRemove={() => removeFile(index)}
                    onOpenModal={() => setModalIndex(index)}
                  />
                ))}
              </div>
            </>
          )}

          {error && <div className="alert alert-error text-sm p-3">{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ padding: '8px 12px', borderTop: '1px solid #ddd', display: 'flex', justifyContent: 'flex-end', gap: '6px', flexShrink: 0 }}>
          <button type="button" className="btn btn-sm" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-sm btn-success" disabled={!files.length || loading} onClick={handleSubmit}>
            {isProcessing ? 'Uploading...' : 'Upload'}
            <FontAwesomeIcon icon={faCheck} />
          </button>
        </div>

      </div>
    </div>
  );
}
